<script setup lang="ts">
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useI18n } from 'vue-i18n'
import { faBarsSort, faInfoCircle, faPenField, faQuestionCircle } from '@fortawesome/duotone-regular-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import { useAppColor } from '~/composables/useAppColor'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'

const {
  item,
  hasForm,
} = defineProps<{
  item: ContentFormWriter
  hasForm: boolean
}>()

const { themeColor } = useAppColor()

const showLabel = defineModel<boolean>('showLabel')
const showDescription = defineModel<boolean>('showDescription')
const showPlaceholder = defineModel<boolean>('showPlaceholder')
const showModelText = defineModel<boolean>('showModelText')

const { t } = useI18n()
</script>

<template>
  <div class="-top-20 left-0 flex flex-wrap items-center gap-2 rounded-xl bg-(--theme-10) p-3 empty:hidden md:absolute md:top-0 md:-left-18 md:flex-col print:hidden">
    <Tooltip
      v-if="item.modelText.destinationContentId && item.enableModelText"
      :content="t('formWriter.tooltips.modelText')"
      side="right"
    >
      <Button
        shape="square"
        :variant="showModelText ? 'solid' : 'outline'"
        :color="themeColor"
        size="lg"
        :data-pendo="PendoFeature.FormWriter.ShowModelText"
        @click="showModelText = !showModelText"
      >
        <font-awesome-icon :icon="faBarsSort" />
      </Button>
    </Tooltip>
    <template v-if="!showModelText && hasForm">
      <Tooltip
        v-if="item.enableLabels"
        :content="t('formWriter.tooltips.label')"
        side="right"
      >
        <Button
          shape="square"
          :variant="showLabel ? 'solid' : 'outline'"
          :color="themeColor"
          size="lg"
          :data-pendo="PendoFeature.FormWriter.ShowLabel"
          @click="showLabel = !showLabel"
        >
          <font-awesome-icon :icon="faInfoCircle" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="item.enableDescription"
        :content="t('formWriter.tooltips.description')"
        side="right"
      >
        <Button
          shape="square"
          :variant="showDescription ? 'solid' : 'outline'"
          :color="themeColor"
          size="lg"
          :data-pendo="PendoFeature.FormWriter.ShowDescription"
          @click="showDescription = !showDescription"
        >
          <font-awesome-icon :icon="faQuestionCircle" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="item.enablePlaceholder"
        :content="t('formWriter.tooltips.placeholder')"
        side="right"
      >
        <Button
          shape="square"
          :variant="showPlaceholder ? 'solid' : 'outline'"
          :color="themeColor"
          size="lg"
          :data-pendo="PendoFeature.FormWriter.ShowPlaceholder"
          @click="showPlaceholder = !showPlaceholder"
        >
          <font-awesome-icon :icon="faPenField" />
        </Button>
      </Tooltip>
    </template>
  </div>
</template>
